import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  useGridApiRef,
} from "@mui/x-data-grid-pro"
import useDashboardRows from "hooks/dashboard/useDashboardRows"
import DataGridLocaleText from "common/DataGridLocaleText"

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    <GridToolbarQuickFilter
      quickFilterParser={searchInput =>
        searchInput.split(",").map(value => value.trim())
      }
      quickFilterFormatter={quickFilterValues => quickFilterValues.join(", ")}
    />
  </GridToolbarContainer>
)

const DashboardDataGrid = ({ projects, dashboardConfigFilters }) => {
  const {
    columns,
    handleColumnOrderChange,
    dashboardConfig,
    handleSortModelChange,
    handleFilterModelChange,
    handleColumnWidthChange,
    handleColumnVisibilityModelChange,
  } = dashboardConfigFilters

  const { filter: filterModel, sort, columnVisibility } = dashboardConfig
  const list = Array.isArray(projects) ? projects : []
  const { rows } = useDashboardRows(list)
  const apiRef = useGridApiRef()
  const lastRefreshedRowsLength = useRef(-1)

  const hasActiveFilters =
    (filterModel?.items?.length ?? 0) > 0 ||
    (filterModel?.quickFilterValues?.length ?? 0) > 0

  useEffect(() => {
    if (!hasActiveFilters) {
      lastRefreshedRowsLength.current = rows.length
      return
    }

    if (rows.length === 0) {
      lastRefreshedRowsLength.current = 0
      return
    }

    if (rows.length === lastRefreshedRowsLength.current) {
      return
    }

    lastRefreshedRowsLength.current = rows.length

    const refreshedFilterModel = {
      ...filterModel,
      items: [...(filterModel?.items ?? [])],
      quickFilterValues: [...(filterModel?.quickFilterValues ?? [])],
    }

    apiRef.current?.setFilterModel?.(refreshedFilterModel)
  }, [apiRef, filterModel, hasActiveFilters, rows.length])

  return (
    <Paper
      sx={{
        height: "80vh",
        width: "100%",
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        pinnedColumns={{ right: ["actions"] }}
        sx={{
          border: 0,
          "& .MuiDataGrid-toolbarContainer>Button": {
            color: "rgba(0, 0, 0, 0.87)",
          },
        }}
        slots={{ toolbar: CustomToolbar }}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        onColumnWidthChange={handleColumnWidthChange}
        sortModel={sort}
        onSortModelChange={handleSortModelChange}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        hideFooterPagination
        hideFooter
        localeText={DataGridLocaleText()}
      />
    </Paper>
  )
}

DashboardDataGrid.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      chapters_count: PropTypes.number,
      costeGeneral: PropTypes.number,
      createdAt: PropTypes.string,
      discount: PropTypes.number,
      fase: PropTypes.number,
      finalPrice: PropTypes.number,
      progressPercentage: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      projectId: PropTypes.string,
    }),
  ).isRequired,
  dashboardConfigFilters: PropTypes.object.isRequired,
}

export default DashboardDataGrid
